import { Drawer, DrawerProps, IconButton, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { CSSProperties, FC, ReactNode, useState } from 'react';
import { WbCard, WbCardContent } from '../WbCard';
import { useDrawersContext } from './DrawersContext';
import { WbDrawerActions } from './WbDrawerActions';
import { WbDrawerContextProvider } from './WbDrawerContext';

const useStyles = makeStyles(
  () => ({
    paper: {
      padding: 0,
      width: '80%',
      overflowX: 'hidden',
    },
  }),
  { name: 'WbDrawer' },
);

interface WbDrawerProps extends DrawerProps {
  children?: ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
  canClose?: boolean;
  title: string;
  headerActions?: ReactNode;
  actions?: ReactNode;
  cardStyle?: CSSProperties;
  builtinDrawerContent?: boolean;
  contentContainerStyle?: CSSProperties;
  titleContainerStyle?: CSSProperties;
  hideHeader?: boolean;
}

export const WbDrawer: FC<WbDrawerProps> = ({
  children,
  title,
  open,
  setOpen,
  canClose = true,
  contentContainerStyle,
  cardStyle,
  titleContainerStyle,
  headerActions,
  actions,
  onClose,
  builtinDrawerContent = true,
  hideHeader = false,
  ...drawerProps
}) => {
  const { onCloseAll } = useDrawersContext();
  const classes = useStyles();

  // while a request to prevent the drawer from closing has been made and not retired yet, there are no requests when === 0
  const [lockRequests, setLockRequests] = useState(0);

  return (
    <WbDrawerContextProvider
      value={{
        setOpen,
        canClose: lockRequests === 0 && canClose,
        open,
        lockClosure: () => setLockRequests(prev => prev + 1),
        unlockClosure: () => setLockRequests(prev => prev - 1),
      }}
    >
      <Drawer
        disableRestoreFocus
        classes={{
          paper: classes.paper,
        }}
        anchor="right"
        open={open}
        onClose={() => {
          if (!canClose) return;
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          onCloseAll ? onCloseAll() : setOpen(false);
        }}
        {...drawerProps}
      >
        <WbCard
          title={hideHeader ? undefined : title}
          cardStyle={{ height: '100%', ...cardStyle }}
          headerContentStyle={{ height: 'auto', padding: '8px 16px' }}
          titleContainerStyle={titleContainerStyle}
          actions={
            hideHeader ? undefined : (
              <>
                {headerActions}
                <IconButton
                  disabled={!canClose}
                  aria-label="clear"
                  onClick={() => {
                    if (!canClose) return;
                    setOpen(false);
                  }}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </>
            )
          }
          hideHeader={hideHeader}
        >
          {builtinDrawerContent ? (
            <WbCardContent
              style={contentContainerStyle || { overflow: 'auto', flex: 1 }}
            >
              {children}
            </WbCardContent>
          ) : (
            children
          )}
          {actions && <WbDrawerActions>{actions}</WbDrawerActions>}
        </WbCard>
      </Drawer>
    </WbDrawerContextProvider>
  );
};
